import { ReactNode, useState } from 'react';

import { Button, Icons } from '@commenda/kakashi';
import { getSignedUrlByFileId } from '@commenda/utils/files/uploadFile';

import { useNotification } from '../hooks/useNotification';

interface ISignedFileLinkProps {
  file: { id?: number; name?: string };
  label?: ReactNode;
  className?: string;
}

/** A file name that opens the file's signed URL in a new tab; plain text when the file has no id. */
export const SignedFileLink = ({
  file,
  label,
  className,
}: ISignedFileLinkProps) => {
  const { id: fileId, name: fileName } = file;
  const { setNotification } = useNotification();
  const [isOpening, setIsOpening] = useState(false);
  if (!fileName) return null;
  if (fileId === undefined) {
    return (
      <span className="block txt-extra-small-regular text-text-quaternary">
        {label ?? fileName}
      </span>
    );
  }

  const notifyOpenFailed = () =>
    setNotification({
      show: true,
      valid: false,
      title: 'Could not open the file',
      message: `We could not open ${fileName}. Try again.`,
    });

  const openSignedFile = async () => {
    setIsOpening(true);
    try {
      const url = await getSignedUrlByFileId(fileId, undefined);
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        notifyOpenFailed();
      }
    } catch {
      notifyOpenFailed();
    } finally {
      setIsOpening(false);
    }
  };

  return (
    <Button
      variant="brand-link"
      size="xs"
      className={className}
      onClick={openSignedFile}
      disabled={isOpening}
      leftIcon={<Icons.LinkExternal01 className="size-3" />}
    >
      {label ?? fileName}
    </Button>
  );
};
